import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { TcGateState } from '@core/models/tc.models';

/**
 * Blocking overlay shown when a material T&C change has not yet been re-accepted
 * (plan §11 + FRD §F.3.2). `disableClose` is inherent — the only exit paths are
 * accepting the new version or waiting for staff action.
 *
 * The Accept button is disabled until the consignor has scrolled to the bottom
 * of the terms body. If the body already fits without overflowing (short doc),
 * we mark it "read" on mount so the button is immediately actionable.
 */
@Component({
  selector: 'app-tc-acceptance-dialog',
  standalone: true,
  imports: [DatePipe, MatProgressSpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tc-acceptance-dialog.component.html',
  styleUrl: './tc-acceptance-dialog.component.scss'
})
export class TcAcceptanceDialogComponent implements AfterViewInit {
  readonly gate = input.required<TcGateState>();
  readonly submitting = input(false);
  readonly accepted = output<string>();
  readonly cancelled = output<void>();

  protected readonly scrollRef = viewChild<ElementRef<HTMLElement>>('scroller');
  protected readonly hasScrolledToEnd = signal(false);

  ngAfterViewInit(): void {
    queueMicrotask(() => this.maybeMarkAsRead());
  }

  onScroll(): void {
    this.maybeMarkAsRead();
  }

  accept(): void {
    if (!this.hasScrolledToEnd() || this.submitting()) return;
    this.accepted.emit(this.gate().currentActive.version);
  }

  cancel(): void {
    if (this.submitting()) return;
    this.cancelled.emit();
  }

  private maybeMarkAsRead(): void {
    const el = this.scrollRef()?.nativeElement;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    if (!hasOverflow) {
      this.hasScrolledToEnd.set(true);
      return;
    }
    const reachedBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
    if (reachedBottom) {
      this.hasScrolledToEnd.set(true);
    }
  }
}
